import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearLoginUser, updateLastActivity } from 'redux/reducers/auth';
import { RootState } from 'redux/store';
import { notifications } from 'notifications';
import { modals } from '@mantine/modals';
import { API } from 'services';

const INACTIVITY_TIMEOUT_MS = 120 * 60 * 1000; // 2 hours
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
// How often to check if the user has been inactive (every minute)
const CHECK_INTERVAL_MS = 60 * 1000;

// Set VITE_AUTO_LOGOUT=false in your .env to disable auto logout during development
const AUTO_LOGOUT_ENABLED = import.meta.env.VITE_AUTO_LOGOUT;

export const useAutoLogout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lastActivity = useSelector(
    (state: RootState) => (state.auth as any).lastActivity as number | null
  );
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef(lastActivity);

  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);

  const handleActivity = useCallback(() => {
    // Throttle dispatches to at most once every 30 seconds to avoid excessive Redux updates
    if (!throttleRef.current) {
      dispatch(updateLastActivity());
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null;
      }, 30_000);
    }
  }, [dispatch]);

  useEffect(() => {
    // Register activity listeners
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    // Periodic inactivity check
    timerRef.current = setInterval(() => {
      if (!AUTO_LOGOUT_ENABLED) return;
      const stored = lastActivityRef.current ?? Date.now();
      const elapsed = Date.now() - stored;
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        notifications.error({
          title: 'Session Expired',
          message: 'You have been logged out due to inactivity.',
        });
        API.logout().finally(() => {
          modals.closeAll();
          dispatch(clearLoginUser());
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1000);
        });
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));
      if (timerRef.current) clearInterval(timerRef.current);
      if (throttleRef.current) clearTimeout(throttleRef.current);
    };
  }, [dispatch, handleActivity, navigate]);
};
